// @ts-check
import { useState } from "react"
import { Download, Loader2, Printer } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useExportDocument } from "@/services/chat-provider/canvas"

/**
 * @param {{ documentId: string, currentVersionId?: string }} props
 */
export default function ExportMenu({ documentId, currentVersionId }) {
  const { mutate: exportDocument, isPending } = useExportDocument(documentId)

  function handleExport(format) {
    exportDocument(
      { format, versionId: currentVersionId },
      {
        onSuccess({ blob, filename }) {
          const url = URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = url
          a.download = filename
          a.click()
          URL.revokeObjectURL(url)
        },
      }
    )
  }

  function handlePrint() {
    // Open the window synchronously (within the click gesture) so browsers
    // don't treat it as an unsolicited popup once the async export resolves.
    const printWindow = window.open("", "_blank")
    exportDocument(
      { format: "pdf", versionId: currentVersionId },
      {
        onSuccess({ blob }) {
          if (!printWindow) return
          const url = URL.createObjectURL(blob)
          printWindow.location.href = url
          printWindow.addEventListener("load", () => printWindow.print())
        },
        onError() {
          printWindow?.close()
        },
      }
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full sm:h-7 sm:w-auto sm:px-2.5 sm:gap-1.5 text-xs" disabled={isPending} title="Export">
          {isPending ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          <span className="hidden sm:inline">Export</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport("pdf")}>
          Export as PDF
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport("latex")}>
          Export as LaTeX
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handlePrint}>
          <Printer size={13} className="mr-1.5" />
          Print
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
